// Stripe integration service for CareerCatalyst
// Handles Stripe initialization, payment intent creation, and coupon validation

// /pure defers Stripe.js script injection until loadStripe() is called
// (payment surfaces), instead of on import — keeps the Stripe cookie and
// script off the landing/marketing pages.
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe } from '@stripe/stripe-js';
import { api } from './api';

// ============================================
// TYPE DEFINITIONS
// ============================================

// Legacy types (kept for backward compatibility)
export interface PaymentIntentResponse {
  client_secret: string;
  payment_intent_id: string;
}

export interface SubscriptionResponse {
  client_secret: string;
  subscription_id: string;
}

// Coupon validation types
export interface CouponValidationResponse {
  valid: boolean;
  coupon_id?: string;
  percent_off?: number;
  amount_off?: number;
  original_amount: number;
  final_amount: number;
  currency: string;
  name?: string;
  error?: string;
}

// Applied coupon details
export interface CouponApplied {
  coupon_id: string;
  name?: string;
  discount: string;
}

// Unified purchase response (supports both payment and zero-cost flows)
export interface PurchaseResponse {
  payment_required: boolean;
  client_secret?: string;
  payment_intent_id?: string;
  subscription_id?: string;
  amount: number;
  original_amount?: number;
  coupon_applied?: CouponApplied;
  credit_id?: string;
  message?: string;
}

// Product types for coupon validation
export type ProductType = 'job_tailoring' | 'message_pack' | 'subscription';

// Lazy-loaded Stripe instance
let stripePromise: Promise<Stripe | null> | null = null;

/**
 * Get Stripe instance (lazy loaded)
 * Initializes Stripe with publishable key from environment
 */
export const getStripe = (): Promise<Stripe | null> => {
  if (!stripePromise) {
    const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

    if (!publishableKey) {
      console.error('VITE_STRIPE_PUBLISHABLE_KEY is not set in environment');
      return Promise.resolve(null);
    }

    stripePromise = loadStripe(publishableKey);
  }

  return stripePromise;
};

/**
 * Stripe service for payment operations
 */
export const stripeService = {
  // ============================================
  // COUPON VALIDATION
  // ============================================

  /**
   * Validate a coupon or promotion code for a specific product
   *
   * @param code - Coupon or promotion code to validate
   * @param productType - Product type ('job_tailoring', 'message_pack', 'subscription')
   * @returns Validation result with original and final amounts
   */
  validateCoupon: async (
    code: string,
    productType: ProductType
  ): Promise<CouponValidationResponse> => {
    return api.post<CouponValidationResponse>('/coupons/validate', {
      code,
      product_type: productType,
    });
  },

  // ============================================
  // PURCHASE OPERATIONS
  // ============================================

  /**
   * Create payment intent for job tailoring credit ($2.99 BETA PRICING)
   * With optional coupon code. If coupon results in $0, credit is provisioned immediately.
   *
   * @param couponCode - Optional coupon or promotion code
   * @returns PurchaseResponse with payment details or immediate provision confirmation
   */
  createJobCreditIntent: async (couponCode?: string): Promise<PurchaseResponse> => {
    return api.post<PurchaseResponse>('/credits/purchase/job', {
      coupon_code: couponCode || null,
    });
  },

  /**
   * Create payment intent for message pack ($1.99)
   * With optional coupon code. If coupon results in $0, pack is provisioned immediately.
   *
   * @param goalId - Goal ID to associate the message pack with
   * @param couponCode - Optional coupon or promotion code
   * @returns PurchaseResponse with payment details or immediate provision confirmation
   */
  createMessagePackIntent: async (
    goalId: string,
    couponCode?: string
  ): Promise<PurchaseResponse> => {
    return api.post<PurchaseResponse>('/credits/purchase/messages', {
      goal_id: goalId,
      coupon_code: couponCode || null,
    });
  },

  /**
   * Create monthly subscription ($14.99/month)
   * With optional coupon code. If 100% off coupon, subscription is activated immediately.
   *
   * @param couponCode - Optional coupon or promotion code
   * @returns PurchaseResponse with subscription details
   */
  createSubscription: async (couponCode?: string): Promise<PurchaseResponse> => {
    return api.post<PurchaseResponse>('/subscription/create', {
      coupon_code: couponCode || null,
    });
  },

  // ============================================
  // STATUS & MANAGEMENT
  // ============================================

  /**
   * Get subscription status and credit balance
   */
  getStatus: async (): Promise<any> => {
    return api.get('/subscription/status');
  },

  /**
   * Cancel subscription at period end
   */
  cancelSubscription: async (): Promise<any> => {
    return api.post('/subscription/cancel');
  },

  /**
   * Reactivate a canceled subscription
   */
  reactivateSubscription: async (): Promise<any> => {
    return api.post('/subscription/reactivate');
  },

  /**
   * Get credit balance
   */
  getCreditBalance: async (): Promise<any> => {
    return api.get('/credits/balance');
  },
};
